import type { SessionDataWithGraphQL, PagingInfoInput, ResolverContext } from "owa-graph-schema";
import type { MailboxInfo } from "owa-client-types";
import { query } from "owa-apollo";
import { ConversationRowsDocument } from "./graphql/__generated__/ConversationRowsQuery.interface";
import type FocusedViewFilter from "owa-service/lib/contract/FocusedViewFilter";
import type SortBy from "owa-mail-list-store/lib/store/schema/SortBy";
import type ViewFilter from "owa-service/lib/contract/ViewFilter";
import { getGqlSortColumnFromOwsSortColumn, getGqlViewFilterFromOwsViewFilter, getGqlFocusedViewFilterFromOwsFocusedViewFilter } from "owa-mail-list-store";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import { mapFindConversationResponseMessageToGql } from "owa-mail-conversation-row-gql-mappers";
import type { RequestOptions } from "owa-service/lib/RequestOptions";
import type FindConversationResponseMessage from "owa-service/lib/contract/FindConversationResponseMessage";
import { addBottleneck } from "owa-performance";
import type { PerformanceDatapoint } from "owa-analytics";
import { returnTopExecutingActionDatapoint } from "owa-analytics";
import { addTimingsToGQLQuery } from "owa-analytics-shared";
import type { ConversationRowsResult } from "./rowConnectionTypes";

export async function getConversationRows(folderId: string, viewFilter: ViewFilter, sortBy: SortBy, canFolderSortByRenewTime: boolean, focusedViewFilter: FocusedViewFilter, shapeName: string, mailboxInfoInput: MailboxInfo, pagingInfoInput: PagingInfoInput, shouldIgnoreSessionData?: boolean, categoryName?: string, searchFolderId?: string, requestOptions?: RequestOptions, initialSessionData?: SessionDataWithGraphQL, isTablePrefetched?: boolean, isReload?: boolean, skipGqlTimings?: boolean): Promise<ConversationRowsResult> {
    // If this is a request for data at the start of the list, try using the session data.
    // If using filterSortBy override, then ignore the session data as the sortby is different.
    if (initialSessionData && !shouldIgnoreSessionData) {
        // In native host, the gql stash is from offline (yet not available) so fall back to the legacy stash if
        // its not available
        if (initialSessionData.conversationRows &&
            initialSessionData.conversationRows.folderId == folderId) {
            return initialSessionData.conversationRows;
        }
        else {
            const sessionDataFindConversationResponse = extractFindConversationResponse(initialSessionData, folderId);
            
            if (sessionDataFindConversationResponse) {
                // If available, return the session data response mapped to the GQL schema
                return mapFindConversationResponseMessageToGql(sessionDataFindConversationResponse, mailboxInfoInput);
            }
        }
    }
    
    const lastSwitchMailFolderDp = returnTopExecutingActionDatapoint((dp: PerformanceDatapoint) => {
        return dp.getEventName() == "SwitchMailFolder";
    });
    
    const result = await addTimingsToGQLQuery(lastSwitchMailFolderDp, {
        isEnabled: !skipGqlTimings,
        discardIfDefined: true,
    }, wfContext => query(ConversationRowsDocument, {
        variables: {
            pagingInfo: pagingInfoInput,
            folderId,
            searchFolderId,
            viewFilter: getGqlViewFilterFromOwsViewFilter(viewFilter),
            focusedViewFilter: getGqlFocusedViewFilterFromOwsFocusedViewFilter(focusedViewFilter),
            sortBy: {
                sortColumn: getGqlSortColumnFromOwsSortColumn(sortBy.sortColumn),
                sortDirection: sortBy.sortDirection,
                canSortByRenewTime: canFolderSortByRenewTime,
            },
            category: categoryName,
            mailboxInfo: mailboxInfoInput,
            shapeName,
            isScheduledFolder: folderIdToName(folderId) == "scheduled",
            pausedInboxTime: null,
        },
        context: {
            requestOptions,
            throttleOperation: isTablePrefetched,
            resolverPolicy: isReload ? "serverOnly" : undefined,
            queryDeduplication: false, // Avoid waiting for a throttled prefetch
            ...wfContext,
        } as ResolverContext,
    }));
    
    return result.data.conversationRows;
}

function extractFindConversationResponse(initialSessionData: SessionDataWithGraphQL, folderId: string): FindConversationResponseMessage | undefined {
    const bottleneck = "fcr";
    const response = initialSessionData.findConversation?.Body;
    let bottleneckReason: string | undefined;
    if (!response) {
        bottleneckReason = "njr";
    }
    else if (response.ResponseClass == "Error") {
        bottleneckReason = "e";
        addBottleneck(bottleneck + "_c", response.ResponseCode || "un");
    }
    else if (folderId != response.FolderId?.Id) {
        bottleneckReason = "wf";
    }
    else if (!response.Conversations) {
        bottleneckReason = "ni";
    }
    else if (typeof response.IndexedOffset != "number" ||
        0 !== response.Conversations.length - response.IndexedOffset) {
        bottleneckReason = "wi";
    }
    
    addBottleneck(bottleneck, bottleneckReason || "s");
    return bottleneckReason ? undefined : response;
}
